<template>
  <section class="twl" :class="{ 'twl--narrow': narrow }" data-test="tasks-workload" aria-label="Team workload">
    <header class="twl__bar">
      <div class="twl__nav">
        <button type="button" class="twl__icon" aria-label="Previous two weeks" data-test="tasks-workload-prev" @click="shift(-1)">
          <svg viewBox="0 0 24 24" width="16" height="16" class="twl__svg" aria-hidden="true" focusable="false"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <button type="button" class="twl__icon" aria-label="Next two weeks" data-test="tasks-workload-next" @click="shift(1)">
          <svg viewBox="0 0 24 24" width="16" height="16" class="twl__svg" aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6" /></svg>
        </button>
        <button type="button" class="twl__today" data-test="tasks-workload-today" @click="goToday">This week</button>
        <h2 class="twl__label" data-test="tasks-workload-label">{{ label }}</h2>
      </div>
    </header>

    <p v-if="!grid.rows.length" class="twl__empty" data-test="tasks-workload-empty">No open tasks fall in these two weeks.</p>

    <div v-else class="twl__scroll">
      <table class="twl__table" data-test="tasks-workload-table">
        <thead>
          <tr>
            <th class="twl__who" scope="col">Teammate</th>
            <th class="twl__late-h" scope="col" title="Open tasks due before this range">Overdue</th>
            <th
              v-for="d in days"
              :key="d"
              scope="col"
              class="twl__day-h"
              :class="{ 'twl__day-h--today': d === ctx.today, 'twl__day-h--weekend': isWeekend(d) }"
            >
              <span class="twl__dow">{{ dowOf(d) }}</span>
              <span class="twl__dnum">{{ Number(d.slice(8, 10)) }}</span>
            </th>
            <th class="twl__total-h" scope="col" title="Open tasks due in these two weeks (overdue not included)" data-test="tasks-workload-total-h">2 weeks</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in grid.rows" :key="r.id || 'unassigned'" data-test="tasks-workload-row" :data-emp="r.id">
            <th class="twl__who" scope="row">{{ r.name }}</th>
            <td class="twl__late-c">
              <button
                v-if="r.overdue"
                type="button"
                class="twl__cell twl__cell--late"
                :class="{ 'is-picked': isPicked(r.id, 'late') }"
                @click="pick(r.id, 'late')"
              >{{ r.overdue }}</button>
            </td>
            <td
              v-for="(n, i) in r.counts"
              :key="days[i]"
              class="twl__c"
              :class="{ 'twl__c--today': days[i] === ctx.today, 'twl__c--weekend': isWeekend(days[i]) }"
            >
              <button
                v-if="n"
                type="button"
                class="twl__cell"
                :class="[`twl__cell--h${level(n)}`, { 'is-picked': isPicked(r.id, days[i]) }]"
                :aria-label="`${r.name}, ${days[i]}: ${n} ${n === 1 ? 'task' : 'tasks'}`"
                data-test="tasks-workload-cell"
                @click="pick(r.id, days[i])"
              >{{ n }}</button>
            </td>
            <td class="twl__total-c">{{ r.total }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="picked" class="twl__detail" data-test="tasks-workload-detail">
      <div class="twl__detail-head">
        <strong>{{ pickedTitle }}</strong>
        <button type="button" class="twl__close" aria-label="Close" @click="picked = null">
          <svg viewBox="0 0 24 24" width="14" height="14" class="twl__svg" aria-hidden="true" focusable="false"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
        </button>
      </div>
      <ul class="twl__tasks">
        <li v-for="t in pickedTasks" :key="t.task.id">
          <button type="button" class="twl__task" data-test="tasks-workload-task" @click="$emit('open', t.task)">
            <span class="twl__task-text">{{ titleOf(t.task) }}</span>
            <span class="twl__task-due">{{ t.dueDay }}</span>
          </button>
        </li>
      </ul>
    </div>

    <p class="twl__hint">Counts are open tasks by due day. A task shared by two people counts for each of them. Click a number to see the tasks.</p>
  </section>
</template>

<script>
/**
 * TasksWorkload — who has how much on, over two weeks (Super Admin preview, 2026-10-16).
 *
 *   <tasks-workload :tasks="…" :ctx="ctx" :employees="employees" :narrow="tkNarrow" @open="tkOpen" />
 *
 * Stands in for a timeline: our tasks have a due DAY, no start date, so there are no bars to draw. One
 * row per teammate (plus "Unassigned"), one column per day, a shaded count per cell on a grid-wide
 * scale, and an Overdue column for open work due before the range. Click a number → the tasks behind it.
 * Read-only: nothing here writes. The maths is src/utils/tasks/calendarView.js workloadRows().
 *
 * Never reads $route / $store. No prop named patNum / siteID / loading (the mixin would shadow it).
 */
import { rowMeta, dueDayOf, assigneeIDs } from '@/utils/tasks/myDay.js';
import {
  workloadRows, workloadDays, heatLevel, spanLabel, addDaysYmd, weekdayIndex, WORKLOAD_DAYS,
} from '@/utils/tasks/calendarView.js';

const WEEK_START = 0;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default {
  name: 'TasksWorkload',
  props: {
    tasks: { type: Array, default: () => [] },
    ctx: { type: Object, required: true },
    employees: { type: Array, default: () => [] },
    narrow: { type: Boolean, default: false },
  },

  data() {
    return { anchor: this.ctx.today, picked: null };
  },

  computed: {
    days() {
      return workloadDays(this.anchor, WEEK_START);
    },
    label() {
      return spanLabel(this.days[0], this.days[this.days.length - 1]);
    },
    // One cheap read per task (due day + assignees); a title is parsed only for the tasks listed under a clicked number.
    entries() {
      return (this.tasks || []).filter(t => t && t.id !== undefined && t.id !== null).map(task => ({
        task,
        dueDay: dueDayOf(task, this.ctx),
        status: task.status,
        assigneeEmpIDs: assigneeIDs(task),
      }));
    },
    people() {
      return (this.employees || []).filter(e => e && e.EmployeeID !== undefined).map(e => ({ id: e.EmployeeID, name: e.Name }));
    },
    grid() {
      return workloadRows(this.entries, this.people, this.days, this.ctx.today);
    },
    pickedTasks() {
      const p = this.picked;
      if (!p) return [];
      const first = this.days[0];
      return this.entries.filter((e) => {
        if (e.status === 'COMPLETED') return false;
        const ids = (e.assigneeEmpIDs || []).map(String);
        const mine = p.id === '' ? ids.length === 0 : ids.indexOf(String(p.id)) > -1;
        if (!mine) return false;
        if (p.key === 'late') return !!e.dueDay && e.dueDay < first && e.dueDay < this.ctx.today;
        return e.dueDay === p.key;
      });
    },
    pickedTitle() {
      const p = this.picked;
      if (!p) return '';
      const row = this.grid.rows.find(r => r.id === String(p.id));
      const who = row ? row.name : '';
      return p.key === 'late' ? `${who} — overdue` : `${who} — ${p.key}`;
    },
  },

  watch: {
    'ctx.today'(now, was) {
      if (this.anchor === was) this.anchor = now;
    },
  },

  methods: {
    level(n) {
      return heatLevel(n, this.grid.max);
    },
    titleOf(task) {
      return rowMeta(task, this.ctx).text || 'Untitled task';
    },
    dowOf(ymd) {
      return DOW[weekdayIndex(ymd)];
    },
    isWeekend(ymd) {
      const w = weekdayIndex(ymd);
      return w === 0 || w === 6;
    },
    shift(dir) {
      this.anchor = addDaysYmd(this.anchor, (dir < 0 ? -1 : 1) * WORKLOAD_DAYS);
      this.picked = null;
    },
    goToday() {
      this.anchor = this.ctx.today;
      this.picked = null;
    },
    pick(id, key) {
      this.picked = this.isPicked(id, key) ? null : { id: String(id), key };
    },
    isPicked(id, key) {
      return !!this.picked && this.picked.id === String(id) && this.picked.key === key;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.twl {
  min-width: 0;
  margin-top: $fu-space-4;
}

.twl__bar {
  display: flex;
  align-items: center;
  margin-bottom: $fu-space-3;
}

.twl__nav {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  min-width: 0;
}

.twl__label {
  margin: 0 0 0 $fu-space-2;
  font-size: 18px;
  font-weight: $fu-fw-bold;
  color: $fu-text-strong;
}

.twl__svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.twl__icon {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: $fu-radius-sm;
  border: 1px solid $fu-border;
  background: $fu-surface;
  color: $fu-neutral-600;
  &:hover { background: $fu-neutral-50; color: $fu-text; }
}

.twl__today {
  @include fu-chip;
  height: 32px;
  padding: 0 12px;
}

.twl__empty {
  margin: $fu-space-6 0;
  text-align: center;
  color: $fu-text-muted;
}

.twl__scroll {
  overflow-x: auto;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-md;
  background: $fu-surface;
}

.twl__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  @include fu-tabular;

  th, td { padding: 6px 4px; text-align: center; border-bottom: 1px solid $fu-border; }
  tbody tr:last-child th, tbody tr:last-child td { border-bottom: 0; }
}

.twl__who {
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 110px;
  max-width: 150px;
  padding-left: 12px !important;
  text-align: left !important;
  background: $fu-surface;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

thead .twl__who { background: $fu-neutral-50; color: $fu-text-muted; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.04em; }

.twl__day-h, .twl__late-h, .twl__total-h {
  min-width: 34px;
  background: $fu-neutral-50;
  font-weight: $fu-fw-semibold;
  color: $fu-text-muted;
}

.twl__late-h { color: $fu-danger-text; }
.twl__dow { display: block; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; }
.twl__dnum { display: block; font-size: 13px; color: $fu-text; }
.twl__day-h--today { background: $fu-brand-50; }
.twl__day-h--today .twl__dnum { color: $fu-brand-700; font-weight: $fu-fw-bold; }
.twl__day-h--weekend, .twl__c--weekend { background-color: $fu-neutral-25; }
.twl__c--today { background-color: $fu-brand-50; }
.twl__total-c { font-weight: $fu-fw-bold; color: $fu-text; }

.twl__cell {
  @include fu-button-reset;
  @include fu-focus-ring;
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  border-radius: $fu-radius-sm;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  &.is-picked { box-shadow: 0 0 0 2px $fu-brand; }
}

.twl__cell--h1 { background: $fu-brand-50; }
.twl__cell--h2 { background: $fu-brand-100; }
.twl__cell--h3 { background: $fu-brand-300; }
.twl__cell--h4 { background: $fu-brand-500; color: #ffffff; }
.twl__cell--late { background: $fu-danger-subtle; color: $fu-danger-text; }

.twl__detail {
  margin-top: $fu-space-4;
  padding: $fu-space-3 $fu-space-4;
  @include fu-card;
}

.twl__detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $fu-space-2;
  color: $fu-text-strong;
}

.twl__close {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: $fu-radius-xs;
  color: $fu-neutral-500;
  &:hover { background: $fu-neutral-100; }
}

.twl__tasks { list-style: none; margin: 0; padding: 0; }

.twl__task {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: $fu-space-3;
  padding: 7px 6px;
  border-radius: $fu-radius-xs;
  text-align: left;
  font-size: 14px;
  color: $fu-text;
  &:hover { background: $fu-neutral-50; }
}

.twl__task-text { min-width: 0; overflow-wrap: anywhere; }
.twl__task-due { flex: none; font-size: 12.5px; color: $fu-text-muted; }

.twl__hint {
  margin: $fu-space-3 0 0;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.twl--narrow .twl__label { font-size: 16px; }
</style>
